import React, { useCallback, useEffect, useMemo, useState } from "react";

// import TablePagination from "@material-ui/core/TablePagination";
import AddIcon from "@material-ui/icons/Add";
import Alert from "@material-ui/lab/Alert";
import { Typography } from "@material-ui/core";
import { useParams } from "react-router-dom";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import styled from "styled-components";
import { DATA_TABLE_NAME, FIELD_TYPES, VISIT_COUNTRY_COLUMNS_WIDTH } from "../constants";
import TableHeader from "./table.header";
import TableRow from "./table.row";
import { isNumberNotEmpty, mapCurrencyValue } from "../../utils/helpers";
import { Accordion, WarningDialog } from "../../../../app/components";
import TextButton from "../../../../app/components/buttons/text";
import { getScheduleOfEventsVisitTableDefaults } from "../../../../app/contexts/helpers";
import { useScheduleEvents } from "../../../../app/contexts/schedule.events.context";
import { useUser } from "../../../../app/contexts/user.context";
import { BUTTON_LABELS, SOE_DELETE_WARNING_MESSAGE } from "../../../../app/components/warning.dialog/messages";
import { getCommentWidth, getCommentType } from "./table.utils";

// const ROWS_PER_PAGE_KEY = "ctpo_SOE_visits_table_rows_per_page";
// const defaultRowsPerPage = () => localStorage.getItem(ROWS_PER_PAGE_KEY)?.toString() || 5;

/*
  This component is responsible for rendering the table of visits for a schedule of events.
  It uses the schedule events context to handle the schedule of events.
  It uses the study context to handle the study plan.
*/
export default function VisitsTable({
  value,
  country,
  setEditOpen,
  fieldType,
  siteId,
  setTableVisibility,
  tableVisibility,
}) {
  const removeColWidth = value === 0 ? 5 : 0;
  const { id, scheduleId } = useParams();
  const {
    scheduleOfEventsVisits,
    setScheduleOfEventsVisits,
    setScheduleOfEventsVisitsChangeLog,
    setVisitsAndAssessmentsMappingChangeLog,
    fetchSOEVisits,
    loadingCountrySOE,
    currentSOE,
  } = useScheduleEvents();
  const { isReadOnlyUser } = useUser();
  const [showOverheadRequiredAlert, setShowOverheadRequiredAlert] = useState(false);
  const [eventVisitIdCounter, setEventVisitIdCounter] = useState(10000);
  const [showWarning, setShowWarning] = useState(false);
  const [elementToDelete, setElementToDelete] = useState(null);
  // const [page, setPage] = useState(0);
  // const [filters, setFilters] = useState({});
  // const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);

  const siteKey = useMemo(() => {
    return (country.siteGroupId && `${country.siteGroupId}-${siteId}`) || "study";
  }, [country.siteGroupId, siteId]);
  
  // const allVisits = scheduleOfEventsVisits[siteKey] || [];

  // const filteredVisits = useMemo(
  //   () => filterVisits(allVisits, filters),
  //   [allVisits, filters]
  // );

  // const paginatedVisits = useMemo(
  //   () => filteredVisits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
  //   [filteredVisits, page, rowsPerPage]
  // );
  
  const isCountry = useMemo(() => {
    return !!country.siteGroupId;
  }, [country]);

  const columnCount = useMemo(() => {
    return country.siteGroupId
      ? 17.5 // Country level
      : fieldType === FIELD_TYPES.COST
      ? 19 // Study level - Overhead % and Overhead columns removed
      : fieldType === FIELD_TYPES.VISIT
      ? 18
      : fieldType === FIELD_TYPES.MEDICAL
      ? 24
      : 20;
  }, [fieldType, country]);

  // /**
  //  * Pagination
  //  */
  // const handleChangePage = (event, newPage) => {
  //   setPage(newPage);
  // };

  // const handleChangeRowsPerPage = (event) => {
  //   const { value } = event.target;
  //   const rows = parseInt(value);

  //   setRowsPerPage(rows);
  //   setPage(0);
  // };

  const updateVisitOverheadPct = () => {
    // Overhead % columns only available at country level
    if (!isCountry) return;
    
    setScheduleOfEventsVisits((prevValues) => {
      if (!prevValues || Object.keys(prevValues).length === 0) return prevValues;

      const updateVisitsWithOverheadPct = (visits) => {
        const firstVisitWithOverheadPct = visits.find(
          (visit) => visit.visitOverheadPct,
        );
        if (!firstVisitWithOverheadPct) {
          setShowOverheadRequiredAlert(true);
          setTimeout(() => setShowOverheadRequiredAlert(false), 4000);
          return visits;
        }
        const firstVisitOverheadPct = firstVisitWithOverheadPct.visitOverheadPct;
        return visits.map((visit) => ({
          ...visit,
          visitOverheadPct: firstVisitOverheadPct,
        }));
      };

      const updatedScheduleOfEventsVisits = { ...prevValues };

      if (!isCountry) {
        if (prevValues.study && prevValues.study.length > 0) {
          updatedScheduleOfEventsVisits.study = updateVisitsWithOverheadPct(
            prevValues.study,
          );
        }
      } else {
        Object.keys(prevValues).forEach((key) => {
          if (
            key !== "study" &&
            prevValues[key] &&
            prevValues[key].length > 0
          ) {
            updatedScheduleOfEventsVisits[key] = updateVisitsWithOverheadPct(
              prevValues[key],
            );
          }
        });
      }
      
      return updatedScheduleOfEventsVisits;
    });

    // Update change logs after state update
    setScheduleOfEventsVisitsChangeLog((prev) => ({
      ...prev,
      [siteKey]: { someValue: "overhead pct change" },
    }));

    setVisitsAndAssessmentsMappingChangeLog({ someValue: "some change" });
  };

  useEffect(() => {
    if (isCountry &&
      !scheduleOfEventsVisits[`${country.siteGroupId}-${siteId}`]
    ) {
      (async () => {
        await fetchSOEVisits(
          scheduleId,
          id,
          isCountry,
          country.siteGroupId,
          siteId,
        );
      })();
    }
    
    if (!isCountry && scheduleOfEventsVisits.study === undefined && scheduleId && id) {
      (async () => {
        await fetchSOEVisits(
          scheduleId,
          id,
          false,
          null,
          null,
          true,
        );
      })();
    }
    // eslint-disable-next-line
  }, [isCountry, country, siteId, scheduleOfEventsVisits, scheduleId, id]);

  const tableContainerRef = React.useRef(null);

  const addNewRow = () => {
    const newId = eventVisitIdCounter + 1;
    const visitList = {
      ...scheduleOfEventsVisits,
      [siteKey]: [
        ...scheduleOfEventsVisits[siteKey],
        {
          ...getScheduleOfEventsVisitTableDefaults(
            parseInt(scheduleId || "0"),
            id,
            {
              visitIntervalType: currentSOE.visitIntervalType,
            },
          ),
          scheduleOfEventsVisitId: newId,
        },
      ],
    }
    setScheduleOfEventsVisits(visitList);

    setScheduleOfEventsVisitsChangeLog((prev) => ({
      ...prev,
      [siteKey]: { someValue: "seq cohort change" },
    }));

    setVisitsAndAssessmentsMappingChangeLog({ someValue: "some change" });

    setEventVisitIdCounter(newId);

    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo({ top: tableContainerRef.current.scrollHeight, behavior: "smooth" });
      }
    }, 200);
  };

  const removeRow = (identifier) => {
    // Find the visit to get its name for the warning message
    const visitToDelete = scheduleOfEventsVisits[siteKey]?.find(
      (sv) => (isNumberNotEmpty(sv.scheduleOfEventsVisitId) ? sv.scheduleOfEventsVisitId : sv.key) === identifier
    );
    
    // Store the identifier and visit name for later deletion
    setElementToDelete({
      identifier,
      name: visitToDelete?.visitName || "this visit"
    });
    
    // Show warning dialog
    setShowWarning(true);
  };

  const performDelete = (identifier) => {
    const visitList = {
      ...scheduleOfEventsVisits,
      [siteKey]: [
        ...scheduleOfEventsVisits[siteKey].filter(
          (sv) =>
            (isNumberNotEmpty(sv.scheduleOfEventsVisitId)
              ? sv.scheduleOfEventsVisitId
              : sv.key) !== identifier,
        ),
      ],
    };
    setScheduleOfEventsVisits(visitList);
    setScheduleOfEventsVisitsChangeLog((prev) => ({
      ...prev,
      [siteKey]: { someValue: "seq cohort change" },
    }));
    setVisitsAndAssessmentsMappingChangeLog({ someValue: "some change" });
  };

  const handleVisitsValueChange = useCallback((name, value, key, type = "number") => {
    const MAX_LENGTH = 250;
    const fieldsToTruncate = [
      "commentsVisit",
      "commentsClinicalStrategy",
      "commentsCost",
      "commentsDataManagement",
    ];

    const visitList = (prevValues) => {
      const newScheduleVisits = prevValues[siteKey]?.map((sv) => {
        if ((sv.scheduleOfEventsVisitId || sv.key) === key) {
          let processedValue;

          if (type === "number" && value &&
            !["visitName", ...fieldsToTruncate].includes(name)
          ) {
            processedValue = parseFloat(parseFloat(value).toFixed(2));
          }

          if (type === "checkbox") {
            processedValue = value === true || value === "true" ? 1 : 0;
          }

          if (fieldsToTruncate.includes(name) && value.length > MAX_LENGTH) {
            processedValue = value.slice(0, MAX_LENGTH);
          }

          if (processedValue === undefined || processedValue === null) {
            processedValue = value;
          }

          return { ...sv, [name]: processedValue };
        }
        return sv;
      });

      return {
        ...prevValues,
        [siteKey]: newScheduleVisits,
      };
    }

    setScheduleOfEventsVisits(visitList);

    setScheduleOfEventsVisitsChangeLog((prev) => ({
      ...prev,
      [siteKey]: { someValue: "seq cohort change" },
    }));

    setVisitsAndAssessmentsMappingChangeLog({ someValue: "some change" });
  }, [
    siteKey,
    setScheduleOfEventsVisits,
    setScheduleOfEventsVisitsChangeLog,
    setVisitsAndAssessmentsMappingChangeLog,
  ]);

  const renderTableRows = () => {
    return (
      scheduleOfEventsVisits[siteKey]?.map((sv, index) =>
      <TableRow
        key={sv.scheduleOfEventsVisitId || sv.key}
        sv={sv}
        removeRow={removeRow}
        onVisitsChange={handleVisitsValueChange}
        value={value}
        index={index}
        fieldType={fieldType}
        columnCount={columnCount}
        siteId={siteId}
        isCountry={isCountry}
        currency={isCountry ? sv?.currency : currentSOE?.currency}
        readonly={isReadOnlyUser}
      />
    ));
  };

const renderSummaryRow = () => {
  if (!isCountry || siteId !== 1) return null;
  
  const visits = scheduleOfEventsVisits[siteKey] || [];
  const totalVisitCost = visits.reduce((sum, visit) => {
    const cost = parseFloat(visit.visitCostTotal) || 0;
    return sum + cost;
  }, 0);

  // Get currency from first visit, fallback to currentSOE, or use empty string if neither exists
  const currency = visits[0]?.currency || currentSOE?.currency || "";

  // Calculate comment width using the same logic as the table rows
  const commentType = getCommentType(fieldType);
  const commentWidth = getCommentWidth(commentType);

  // Calculate the width for the label cell (all columns except the last value column)
  const calculateLabelWidth = () => {
    if (value === 0) {
      // Country level columns: Visit Category (12%) + Visit Name (18%) + Study Day (6%) + (+/-) (6%) + Visit Window (6%) + Overhead % (6%) + Overhead (6%)
      return "60%";
    } else {
      // Country level columns: Visit Category (25%) + Visit Name (25%) + Study Day (10%) + (+/-) (10%) + Visit Window (10%) + Overhead % (10%) + Overhead (10%)
      return "100%"; // This will be adjusted based on whether we're showing the total
    }
  };

  return (
    <SummaryRow>
      {/* Label spanning across all columns except the total */}
      <SummaryCell width={calculateLabelWidth()} showBorder={false} alignRight={true} style={{ paddingRight: '20px' }}>
        <Typography variant="body1" style={{ whiteSpace: 'nowrap' }}>
          <strong>Total Cost Per Completed Participant</strong>
        </Typography>
      </SummaryCell>
      
      {/* Total Visit Cost */}
      {(fieldType === FIELD_TYPES.COST || value !== 0) && isCountry && (
        <SummaryCell width={value === 0 ?
          VISIT_COUNTRY_COLUMNS_WIDTH.totalVisitCost.expanded :
          VISIT_COUNTRY_COLUMNS_WIDTH.totalVisitCost.collapsed
        } showBorder={true}>
          <Typography variant="body1">
            <strong>{currency ? mapCurrencyValue(currency, totalVisitCost.toFixed(2)) : totalVisitCost.toFixed(2)}</strong>
          </Typography>
        </SummaryCell>
      )}

      {/* Additional columns for value === 0 - all empty since we're spanning */}
      {value === 0 && (
        <>
          {fieldType === FIELD_TYPES.MEDICAL && (
            <>
              {/* 100% SDR/SDV Time */}
              <SummaryCell width="6%">
                {/* Empty cell */}
              </SummaryCell>
              
              {/* CRA Sampled Visit */}
              <SummaryCell width="6%">
                {/* Empty cell */}
              </SummaryCell>
              
              {/* Onsite SDR/SDV */}
              <SummaryCell width="6%">
                {/* Empty cell */}
              </SummaryCell>
              
              {/* CDA Review */}
              <SummaryCell width="6%">
                {/* Empty cell */}
              </SummaryCell>
            </>
          )}

          {fieldType === FIELD_TYPES.DATA_MANAGEMENT && (
            <>
              {/* Unique CRFs */}
              <SummaryCell width="6%">
                {/* Empty cell */}
              </SummaryCell>
              
              {/* Non-unique CRFs */}
              <SummaryCell width="6%">
                {/* Empty cell */}
              </SummaryCell>
            </>
          )}
          
          {/* Comments column */}
          <SummaryCell width={commentWidth}>
            {/* Empty cell */}
          </SummaryCell>
          
          {/* Remove button column */}
          <SummaryCell width={VISIT_COUNTRY_COLUMNS_WIDTH.deleteButton}>
            {/* Empty cell */}
          </SummaryCell>
        </>
      )}
    </SummaryRow>
  );
};

  const onDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (
      destination.draggableId === source.draggableId &&
      destination.index === source.index
    )
      return;

    const newOrderedVisits = scheduleOfEventsVisits["study"];
    const reorderedVisit = newOrderedVisits[source.index];
    newOrderedVisits.splice(source.index, 1);
    newOrderedVisits.splice(destination.index, 0, reorderedVisit);
    const visitList = {
      ...scheduleOfEventsVisits,
      study: [...newOrderedVisits],
    }
    setScheduleOfEventsVisits(visitList);
    setScheduleOfEventsVisitsChangeLog((prev) => ({
      ...prev,
      [siteKey]: { someValue: "some change" },
    }));
  };

  // function filterVisits(visits, filters) {
  //   if (!filters || Object.keys(filters).length === 0) return visits;
  //   return visits.filter((visit) =>
  //     Object.entries(filters).every(([key, filter]) => {
  //       if (!filter.value) return true;
  //       const fieldValue = (visit[key] || "").toString().toLowerCase();
  //       return fieldValue.includes(filter.value.toLowerCase());
  //     })
  //   );
  // }
  
  const handleWarningConfirm = () => {
    if (elementToDelete && elementToDelete.identifier) {
      performDelete(elementToDelete.identifier);
    }
    setElementToDelete(null);
    setShowWarning(false);
  };

  const handleWarningCancel = () => {
    setElementToDelete(null);
    setShowWarning(false);
  };

  return (
    <>
      {!loadingCountrySOE && (
        <Accordion
          scrollIntoView={true}
          width={"100%"}
          summary={"Visits"}
          defaultExpanded={tableVisibility.showVisitTable}
          onClick={() => {
            setTableVisibility("showVisitTable");
          }}
          data-table-name={DATA_TABLE_NAME.VISITS_TABLE}
        >
          {tableVisibility.showVisitTable && (
            <Container
              noBottom={isCountry}
              key={country.siteGroupId || "study"}
              onClick={(event) => event.stopPropagation()}
              data-table-name={DATA_TABLE_NAME.VISITS_TABLE}
            >
              {!isCountry ? (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Table
                    data-table-container="true"
                    data-table-name={DATA_TABLE_NAME.VISITS_TABLE}
                    ref={tableContainerRef}
                  >
                    <TableHeader
                      setEditOpen={setEditOpen}
                      visitToUse={currentSOE}
                      value={value}
                      removeColWidth={removeColWidth}
                      fieldType={fieldType}
                      columnCount={columnCount}
                      setOverheadPct={updateVisitOverheadPct}
                      isCountry={isCountry}
                      readOnly={isReadOnlyUser}
                    />
                    <Droppable droppableId="visits">
                      {(provided) => (
                        <RowsContainer
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {renderTableRows()}
                          {provided.placeholder}
                        </RowsContainer>
                      )}
                    </Droppable>
                  </Table>
                </DragDropContext>
              ) : (
                <Table
                  data-table-container="true"
                  data-table-name={DATA_TABLE_NAME.VISITS_TABLE}
                  ref={tableContainerRef}
                >
                  <TableHeader
                    setEditOpen={setEditOpen}
                    visitToUse={currentSOE}
                    value={value}
                    removeColWidth={removeColWidth}
                    fieldType={fieldType}
                    columnCount={columnCount}
                    setOverheadPct={updateVisitOverheadPct}
                    isCountry={isCountry}
                    readOnly={isReadOnlyUser}
                  />
                  {renderTableRows()}
                  {renderSummaryRow()}
                </Table>
              )}
              <FlexRow>
                {(!isReadOnlyUser && value === 0) && (
                  <ButtonContainer>
                    <TextButton
                      sx={{ width: "4rem" }}
                      style={{ marginLeft: "0.4rem" }}
                      onClick={addNewRow}
                      startIcon={<AddIcon />}
                    >
                      Add Row
                    </TextButton>
                  </ButtonContainer>
                )}
                {/* <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  rowsPerPage={rowsPerPage}
                  page={page}
                  count={filteredVisits.length}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                /> */}
              </FlexRow>
            </Container>
          )}
        </Accordion>
      )}
      {showOverheadRequiredAlert && (
        <div
          style={{
            position: "fixed",
            left: 0,
            bottom: 0,
            margin: "20px",
            zIndex: 1000,
          }}
        >
          <Alert severity="error">
            Enter a Visit Overhead % on at least 1 visit.
          </Alert>
        </div>
      )}
      
      <WarningDialog
        open={showWarning}
        onConfirm={handleWarningConfirm}
        onCancel={handleWarningCancel}
        message={
          SOE_DELETE_WARNING_MESSAGE(elementToDelete?.name, "visit")
        }
        confirmLabel={BUTTON_LABELS.DELETE}
      />
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid var(--surface-300);
  border-top: none;
  border-bottom: ${(p) =>
    p.noBottom ? "none" : "1px solid var(--surface-300)"};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const ButtonContainer = styled.div`
  margin: 1rem 0rem;
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--surface-300);
  width: 100%;
  max-height: 600px;
  overflow-y: auto;
  position: relative;
  &:last-child {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  }
}
`;

const RowsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: stretch;
  min-height: 60px;
  width: 100%;  
  border-bottom: 1px solid var(--surface-300);
  
  &:last-child {
    border-bottom: none;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const SummaryCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => p.alignRight ? 'flex-end' : 'center'};
  justify-content: center;
  min-height: 60px;
  width: ${(p) => (typeof p.width === "function" ? p.width() : p.width)};
  padding: 8px 5px;
  text-align: ${(p) => p.alignRight ? 'right' : 'center'};
  border-left: ${(p) => p.showBorder ? "1px solid var(--surface-300)" : "none"};
  
  &:first-child {
    border-bottom-left-radius: 8px;
  }
  
  &:last-child {
    border-bottom-right-radius: 8px;
  }
  
  > * {
    text-align: ${(p) => p.alignRight ? 'right' : 'center'};
    width: 100%;
  }
`;